import React, { useCallback, useEffect, useState } from 'react';

import {
  AuditLogEntidade,
  AuditLogItem,
  fetchAuditLogs,
} from '../../api/auditLogsApi';
import { notify } from '../../shared/feedback/notify';

import '../../shared/feedback/erro-de-tela.css';
import './HistoricoAlteracoes.css';

const POR_PAGINA = 20;

const ERRO_CARGA =
  'Não foi possível carregar o histórico de alterações. A lista abaixo não representa o que já foi ' +
  'alterado. Recarregue a página; se persistir, acione a TI.';

interface HistoricoAlteracoesProps {
  isOpen: boolean;
  onClose: () => void;
  /** Entidade cujo histórico exibir. Precisa estar na whitelist do backend. */
  entity: AuditLogEntidade;
  /** Restringe a um registro. Ausente = histórico de todos os registros da entidade. */
  entityId?: string;
  titulo: string;
  /** Frase curta explicando o que entra nesta lista (a hint embutida deste bloco). */
  descricao: string;
  /** Cabeçalho da coluna do alvo. Ex: "Usuário", "Permissão". */
  rotuloAlvo: string;
}

const formatarDataHora = (iso: string): string => {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Histórico de alterações de uma entidade de configuração (at-69, BUG-V60/V61).
 *
 * Um componente para os dois pontos de uso (Usuários e a matriz de permissões do
 * Checklist), porque a pergunta é a mesma nos dois — "quem mudou o quê, quando, de que
 * para quê" — e o backend já entrega tudo humanizado. Duplicar aqui significaria dois
 * lugares para corrigir quando a próxima entidade entrar na whitelist.
 */
const HistoricoAlteracoes: React.FC<HistoricoAlteracoesProps> = ({
  isOpen,
  onClose,
  entity,
  entityId,
  titulo,
  descricao,
  rotuloAlvo,
}) => {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (paginaAlvo: number) => {
    setCarregando(true);
    try {
      const resposta = await fetchAuditLogs({
        entity,
        entityId,
        page: paginaAlvo,
        limit: POR_PAGINA,
      });
      setLogs(resposta.logs);
      setTotal(resposta.total);
      setErro(null);
    } catch {
      // Estado seguro + sinal (at-60). O `erro` também gateia a frase "nenhuma alteração
      // registrada" mais abaixo: numa tela de histórico, "não há registro" é uma
      // afirmação séria, e ela não pode sair de uma consulta que não respondeu.
      // ⚠️ Texto FIXO, não `errorFrom` (regra da at-65): o que importa aqui é a
      // consequência — a lista abaixo não representa o que já foi alterado —, e num 403 o
      // extrator devolveria "Você não tem permissão", que não orienta nada numa tela que o
      // usuário conseguiu abrir.
      setLogs([]);
      setTotal(0);
      setErro(ERRO_CARGA);
      notify.error(ERRO_CARGA, { toastId: `historico-${entity}` });
    } finally {
      setCarregando(false);
    }
  }, [entity, entityId]);

  useEffect(() => {
    if (!isOpen) return;
    setPagina(1);
    carregar(1);
  }, [isOpen, carregar]);

  if (!isOpen) return null;

  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const irPara = (destino: number) => {
    setPagina(destino);
    carregar(destino);
  };

  return (
    <div className="historico-overlay" role="presentation" onClick={onClose}>
      <div
        className="historico-modal"
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="historico-header">
          <h3 className="historico-titulo">{titulo}</h3>
          <button type="button" className="historico-fechar" onClick={onClose} aria-label="Fechar">
            Fechar
          </button>
        </div>

        <div className="historico-body">
          <p className="historico-hint">{descricao}</p>

          {erro && <div className="erro-de-tela" role="alert">{erro}</div>}

          {carregando && <div className="historico-vazio">Carregando…</div>}

          {!carregando && !erro && logs.length === 0 && (
            <div className="historico-vazio">Nenhuma alteração registrada até agora.</div>
          )}

          {!carregando && logs.length > 0 && (
            <ul className="historico-lista">
              {logs.map((log) => (
                <li key={log.id} className="historico-item">
                  <div className="historico-item-topo">
                    <span className="historico-acao">{log.actionLabel}</span>
                    <span className="historico-data">{formatarDataHora(log.createdAt)}</span>
                  </div>

                  <div className="historico-item-meta">
                    <span>
                      <strong>{rotuloAlvo}:</strong> {log.alvo || 'Registro removido'}
                    </span>
                    <span>
                      <strong>Feito por:</strong> {log.performedByName || 'Sistema'}
                    </span>
                  </div>

                  {log.detalhes.length > 0 && (
                    <ul className="historico-detalhes">
                      {log.detalhes.map((detalhe, indice) => (
                        <li key={`${log.id}-${indice}`}>
                          <strong>{detalhe.rotulo}:</strong>{' '}
                          {detalhe.de === null && detalhe.para === null ? (
                            // Troca de senha: o registro existe, os valores nunca são guardados.
                            <span className="historico-sem-valor">valor não registrado</span>
                          ) : (
                            <>
                              <span className="historico-de">{detalhe.de ?? 'vazio'}</span>
                              {' → '}
                              <span className="historico-para">{detalhe.para ?? 'vazio'}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {!erro && total > POR_PAGINA && (
          <div className="historico-paginacao">
            <button
              type="button"
              className="historico-pagina-btn"
              disabled={pagina === 1 || carregando}
              onClick={() => irPara(pagina - 1)}
            >
              Anterior
            </button>
            <span className="historico-pagina-info">
              Página {pagina} de {totalPaginas} ({total} registros)
            </span>
            <button
              type="button"
              className="historico-pagina-btn"
              disabled={pagina >= totalPaginas || carregando}
              onClick={() => irPara(pagina + 1)}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricoAlteracoes;
